/**
 * Auth context: user/balance from /api/auth/me. Single source of truth.
 * Parallelizes auth + financial fetch to eliminate loading waterfall.
 * Shows lastKnownBalance from localStorage immediately for perceived performance.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { apiRequest } from "../api/apiClient";
import { mapErrorToUserMessage } from "../utils/authErrors";
import { clearApiCache } from "../utils/apiClient";

const LAST_KNOWN_BALANCE_KEY = "estatecoin_lastKnownBalance";

const AuthContext = createContext({
  user: null,
  balance: 0,
  availableBalance: 0,
  reservedInOpenBuyOrders: 0,
  portfolioValue: 0,
  refreshFinancial: async () => {},
  loadingAuth: true,
  loading: true,
  forcePasswordReset: false,
  login: async () => {},
  logout: async () => {},
  refreshUser: async () => {},
  fetchMe: async () => {},
  tradingSessionToken: 0,
  resetTradingContext: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [walletBalance, setWalletBalance] = useState(() => {
    try {
      const cached = localStorage.getItem(LAST_KNOWN_BALANCE_KEY);
      return cached != null ? parseFloat(cached) : null;
    } catch {
      return null;
    }
  });
  const [portfolioValue, setPortfolioValue] = useState(null);
  const [reservedInOpenBuyOrders, setReservedInOpenBuyOrders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [forcePasswordReset, setForcePasswordReset] = useState(false);
  const [tradingSessionToken, setTradingSessionToken] = useState(0);
  const financialCache = useRef({ data: null, timestamp: 0 });
  const FINANCIAL_CACHE_TTL_MS = 30000;
  const lastAuthMeRef = useRef(0);
  const userRef = useRef(null);
  const AUTH_ME_MIN_INTERVAL_MS = 30000; // Min 30s between /api/auth/me to avoid DDoS triggers
  userRef.current = user;

  const resetTradingSession = useCallback(() => {
    setTradingSessionToken((t) => t + 1);
  }, []);

  const fetchMe = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && now - lastAuthMeRef.current < AUTH_ME_MIN_INTERVAL_MS) {
      return userRef.current ?? null;
    }
    lastAuthMeRef.current = now;
    try {
      const data = await apiRequest("/api/auth/me", { method: "GET" }).catch(() => null);
      setUser(data?.user ?? null);
      if (data?.forcePasswordReset) setForcePasswordReset(true);
      return data?.user ?? null;
    } catch (err) {
      setUser(null);
      return null;
    } finally {
      setLoadingAuth(false);
      setLoading(false);
    }
  }, []);

  const login = useCallback(
    async (email, password) => {
      try {
        let data;
        try {
          data = await apiRequest("/api/auth/login", {
            method: "POST",
            body: JSON.stringify({ email, password, rememberMe: true }),
          });
        } catch (err) {
          const status = err?.status ?? 401;
          const errData = err?.data ?? {};
          const errCode = errData?.error ?? errData?.code ?? null;
          const e = new Error(mapErrorToUserMessage({ status, data: errData }));
          e.code = errCode;
          e.supportId = errData?.supportId ?? null;
          throw e;
        }
        if (!data) {
          throw new Error(mapErrorToUserMessage({ status: 401 }));
        }
        if (data?.token) {
          localStorage.setItem("token", data.token);
        }
        if (data?.forcePasswordReset === true) {
          setForcePasswordReset(true);
          setUser(data.user ?? null);
          resetTradingSession();
          return data;
        }
        const meData = await apiRequest("/api/auth/me", { method: "GET" }).catch(() => null);
        if (meData?.user) {
          setUser(meData.user);
          setForcePasswordReset(!!meData.forcePasswordReset);
        } else {
          setUser(data.user ?? null);
        }
        resetTradingSession();
        return data;
      } catch (err) {
        const message = err?.message || mapErrorToUserMessage(err);
        const e = new Error(message);
        e.supportId = err?.supportId;
        e.code = err?.code ?? null;
        throw e;
      }
    },
    [resetTradingSession]
  );

  const logout = useCallback(async () => {
    financialCache.current = { data: null, timestamp: 0 };
    clearApiCache();
    localStorage.removeItem("token");
    localStorage.removeItem(LAST_KNOWN_BALANCE_KEY);
    try {
      await apiRequest("/api/auth/logout", { method: "POST" });
    } catch (e) {
      console.error("[auth] Logout error:", e);
    } finally {
      setUser(null);
      setWalletBalance(null);
      setPortfolioValue(null);
      setReservedInOpenBuyOrders(0);
      setForcePasswordReset(false);
      setLoading(false);
      setLoadingAuth(false);
      resetTradingSession();
      window.location.href = "/login";
    }
  }, [resetTradingSession]);

  const refreshUser = useCallback(async () => {
    const u = await fetchMe();
    return u;
  }, [fetchMe]);

  const refreshingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refreshFinancial = useCallback(async (force = false) => {
    const now = Date.now();
    const cached = financialCache.current;
    if (!force && cached.data && (now - cached.timestamp) < FINANCIAL_CACHE_TTL_MS) {
      if (mountedRef.current) {
        setWalletBalance(Number(cached.data.balance ?? 0));
        setPortfolioValue(Number(cached.data.portfolioValue ?? 0));
        setReservedInOpenBuyOrders(Number(cached.data.reservedInOpenBuyOrders ?? 0));
      }
      return;
    }
    if (refreshingRef.current) return;
    refreshingRef.current = true;

    try {
      const data = await apiRequest("/api/user/financial").catch((err) => {
        console.error("Financial API failed", err);
        return null;
      });
      if (!data) return;
      financialCache.current = { data, timestamp: Date.now() };
      const bal = Number(data.balance ?? 0);
      if (mountedRef.current) {
        setWalletBalance(bal);
        setPortfolioValue(Number(data.portfolioValue ?? 0));
        setReservedInOpenBuyOrders(Number(data.reservedInOpenBuyOrders ?? 0));
      }
      try {
        localStorage.setItem(LAST_KNOWN_BALANCE_KEY, String(bal));
      } catch (_) {}
    } catch (err) {
      console.error("Financial refresh failed", err);
    } finally {
      refreshingRef.current = false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      let authResult = null;
      try {
        authResult = await apiRequest("/api/auth/me", { method: "GET" });
      } catch (err) {
        if (err?.status !== 401) {
          console.error("Auth check failed:", err);
        }
        if (cancelled) return;
    setUser(null);
      setWalletBalance(null);
      setPortfolioValue(null);
      setReservedInOpenBuyOrders(0);
      setLoadingAuth(false);
        setLoading(false);
        return;
      }
      if (cancelled) return;
      const u = authResult?.user ?? null;
      setUser(u);
      if (authResult?.forcePasswordReset) setForcePasswordReset(true);
      setLoadingAuth(false);
      setLoading(false);
      if (!u) {
        setWalletBalance(null);
        setPortfolioValue(null);
        setReservedInOpenBuyOrders(0);
        return;
      }
      // Only fetch financial AFTER confirming user is authenticated
      try {
        const financialResult = await Promise.race([
          apiRequest("/api/user/financial", { method: "GET" }),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 3000)),
        ]);
        if (cancelled) return;
        if (u?._id && financialResult) {
          const bal = Number(financialResult.balance ?? 0);
          const pv = Number(financialResult.portfolioValue ?? 0);
          const reserved = Number(financialResult.reservedInOpenBuyOrders ?? 0);
          setWalletBalance(bal);
          setPortfolioValue(pv);
          setReservedInOpenBuyOrders(reserved);
          financialCache.current = { data: financialResult, timestamp: Date.now() };
          try {
            localStorage.setItem(LAST_KNOWN_BALANCE_KEY, String(bal));
          } catch (_) {}
        }
      } catch (_) {
        if (cancelled) return;
        try {
          const lastKnown = localStorage.getItem(LAST_KNOWN_BALANCE_KEY);
          if (lastKnown != null) setWalletBalance(parseFloat(lastKnown));
        } catch (_) {}
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (user?._id && walletBalance == null) refreshFinancial();
  }, [user?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [cachedBalance] = useState(() => {
    try {
      const s = localStorage.getItem(LAST_KNOWN_BALANCE_KEY);
      return s != null ? Number(s) : null;
    } catch { return null; }
  });

  const balance = walletBalance != null ? walletBalance : (cachedBalance ?? user?.balance ?? 0);
  // Spendable amount — from financial API only; never use user?.balance (can be stale, excludes reserved)
  const availableBalance = walletBalance != null ? walletBalance : (cachedBalance ?? 0);

  const value = useMemo(
    () => ({
      user,
      balance,
      availableBalance,
      reservedInOpenBuyOrders: reservedInOpenBuyOrders ?? 0,
      walletBalance: walletBalance ?? 0,
      portfolioValue: portfolioValue ?? 0,
      setWalletBalance,
      refreshFinancial,
      loadingAuth,
      loading,
      forcePasswordReset,
      login,
      logout,
      refreshUser,
      fetchMe,
      tradingSessionToken,
      resetTradingContext: resetTradingSession,
    }),
    [
      user,
      balance,
      availableBalance,
      reservedInOpenBuyOrders,
      walletBalance,
      portfolioValue,
      loadingAuth,
      loading,
      forcePasswordReset,
      login,
      logout,
      refreshUser,
      fetchMe,
      tradingSessionToken,
      resetTradingSession,
      refreshFinancial,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
